/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Resource Lifecycle State family (operator-cop-doctrine
 *  §3A.7 / §8.4; R.4.2 full 7-state IMS Resource Status Lifecycle).
 *
 *  Single source of the badge palette for the IMS Resource Status Lifecycle
 *  (available → requested → assigned → checked_in → staging → committed →
 *  demobilized). Consumed by the "Nearby Response Resources" badge (Task #93
 *  unification: the operator-facing badge reads lifecycle_state, NOT the legacy
 *  `status` flag) and any future lifecycle-state chips.
 *
 *  Palette (locked law — do NOT re-tune the hex values here):
 *    available   grey  #94a3b8   (idle / ready, not yet committed)
 *    requested   blue  #38bdf8   (a request has been raised against it)
 *    assigned    amber #f59e0b   (bound to a request, en route to the incident)
 *    checked_in  lt-blue #7dd3fc (reported in at the incident)
 *    staging     amber #f59e0b   (moved into a staging area)
 *    committed   red   #ef4444   (committed to the incident — active)
 *    demobilized grey  #64748b   (released; terminal)
 *
 *  These hues deliberately echo the Response Level family's grey/blue/amber/red
 *  (and overlap freezing-rain/severe-storm CAP hues) — that's the §3A.7 overlap
 *  debt. The lock keeps it honest: this module owns its own hex constants and
 *  must NOT import another family's tokens, and colour is never load-bearing
 *  alone — every badge pairs the colour with the doctrinal state name.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots (enforced by the no-restricted-imports
 *     lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

import type { ResourceLifecycleState } from '@tsr/shared-types';

export interface LifecycleStateStyle {
  /** CSS hex for the badge text + tinted background. */
  color: string;
  /** Short doctrinal state name (Title Case), shown in the badge. */
  label: string;
}

/** The locked palette — one treatment per lifecycle state. */
const LIFECYCLE_STATE_STYLES: Record<ResourceLifecycleState, LifecycleStateStyle> = {
  available: { color: '#94a3b8', label: 'Available' },
  requested: { color: '#38bdf8', label: 'Requested' },
  assigned: { color: '#f59e0b', label: 'Assigned' },
  checked_in: { color: '#7dd3fc', label: 'Checked In' },
  staging: { color: '#f59e0b', label: 'Staging' },
  committed: { color: '#ef4444', label: 'Committed' },
  demobilized: { color: '#64748b', label: 'Demobilized' },
};

/** Fallback for an unmapped / null lifecycle_state (neutral grey, raw label). */
const UNKNOWN_STATE: LifecycleStateStyle = { color: '#94a3b8', label: 'Unknown' };

function isLifecycleState(v: unknown): v is ResourceLifecycleState {
  return typeof v === 'string' && v in LIFECYCLE_STATE_STYLES;
}

/**
 * Resolve the badge treatment for a lifecycle_state value. Tolerant of null /
 * unknown (resolves to the neutral grey 'Unknown' style, never dropped); an
 * unmapped non-empty string keeps its raw text so a new server state still reads.
 */
export function lifecycleStateStyle(state: string | null | undefined): LifecycleStateStyle {
  if (isLifecycleState(state)) return LIFECYCLE_STATE_STYLES[state];
  if (typeof state === 'string' && state.length > 0) {
    return { color: UNKNOWN_STATE.color, label: state.replace(/_/g, ' ') };
  }
  return UNKNOWN_STATE;
}
